import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useAccessData } from '../hooks/useAccessData.js'

// Constants to prevent recreation
const NAV_ITEMS = [
  {
    id: 'dashboard',
    path: '/dashboard',
    icon: 'bi-speedometer2',
    label: 'Dashboard',
    description: 'Real-time monitoring',
  },
  {
    id: 'analysis',
    path: '/analysis',
    icon: 'bi-graph-up',
    label: 'Analysis',
    description: 'Data trends',
  },
  {
    id: 'configuration',
    path: '/configuration',
    icon: 'bi-gear',
    label: 'Configuration',
    description: 'Device settings',
    requiresOnline: true,
  },
  {
    id: 'device-settings',
    path: '/device-settings',
    icon: 'bi-info-circle',
    label: 'Device Info',
    description: 'Naming & status',
  },
  {
    id: 'access-management',
    path: '/access-management',
    icon: 'bi-people',
    label: 'Access Management',
    description: 'Permissions & sharing',
  },
]

const ICON_STYLE = { width: '16px', height: '16px', display: 'flex', alignItems: 'center' }
const LOGO_STYLE = { 
  height: '1.75rem', // Slightly larger than text for visual prominence
  width: 'auto', 
  display: 'flex',
  alignItems: 'center',
}

/**
 * Left Navigation component for device-specific tools
 * Optimized for React 19+ with fixed optimal width
 */
function LeftNavigation() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { selectedDeviceId, currentDeviceName } = useCloudContext()
  const { useDeviceAccessRequests } = useAccessData()
  const { data: accessRequests = [] } = useDeviceAccessRequests(selectedDeviceId)
  const badgeCount = accessRequests.length

  const handleNavClick = useCallback((path) => {
    navigate(path)
  }, [navigate])
  
  // Don't render if no device selected
  if (!selectedDeviceId) {
    return null
  }

  return (
    <div 
      className="h-100 d-flex flex-column" 
      style={{ 
        width: '220px', 
        minWidth: '220px', 
        maxWidth: '220px',
        boxSizing: 'border-box',
        borderRight: '1px solid var(--nav-border)',
        position: 'relative',
      }}
    >
      <style>
        {`
          .nav-item-enhanced {
            transition: all 0.2s ease;
            background-color: transparent;
          }
          
          .nav-item-enhanced:hover:not(.active) {
            background-color: var(--nav-hover-bg) !important;
            transform: translateX(1px);
          }
          
          .nav-item-enhanced.active {
            background: var(--nav-active-bg) !important;
            box-shadow: 0 2px 8px var(--color-active-shadow);
            transform: translateX(0px);
          }
          
          .nav-item-enhanced:focus {
            outline: 2px solid var(--color-focus-ring);
            outline-offset: 2px;
          }
          
          .device-header-enhanced {
            background: var(--bg-header-gradient);
            border-bottom: 1px solid var(--border-color-dark);
            min-height: fit-content;
            padding: 0.875rem 1rem !important;
            animation: deviceHeaderFadeIn 0.4s ease-out;
          }
          
          .support-item-enhanced {
            transition: all 0.2s ease;
          }
          
          .support-item-enhanced:hover:not(.active) {
            background-color: var(--color-hover-light) !important;
          }
          
          .device-name-adaptive {
            font-size: clamp(0.9rem, 1.25rem, 1.25rem);
            line-height: 1.3;
            max-height: 3.2rem;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            word-break: break-word;
            animation: deviceNameSlideIn 0.3s ease-out 0.1s both;
          }
          
          .device-icon-animated {
            animation: deviceIconScale 0.3s ease-out;
          }
          
          @keyframes deviceHeaderFadeIn {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes deviceNameSlideIn {
            from {
              opacity: 0;
              transform: translateX(-12px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes deviceIconScale {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      
      {/* Device Context Header */}
      <div className="device-header-enhanced d-flex align-items-center">
        <img 
          src="/images/Altion-final.svg" 
          alt="Altion Device" 
          className="me-2 flex-shrink-0 device-icon-animated"
          style={LOGO_STYLE}
          key={selectedDeviceId}
        />
        <h5 className="mb-0 fw-bold device-name-adaptive" title={currentDeviceName} key={`name-${selectedDeviceId}`}>
          {currentDeviceName}
        </h5>
      </div>
      
      {/* Navigation Items */}
      <nav className="flex-grow-1" style={{ padding: '0.5rem 0.375rem', overflow: 'hidden' }}>
        <div className="list-group list-group-flush">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname === item.path
            
            return (
              <button
                key={item.id}
                className={`list-group-item list-group-item-action border-0 rounded nav-item-enhanced ${
                  isActive ? 'active' : ''
                }`}
                onClick={() => handleNavClick(item.path)}
                title={item.description}
                style={{ 
                  width: '100%',
                  margin: '0 0 0.1875rem 0',
                  padding: '0.8125rem 0.75rem',
                  boxSizing: 'border-box',
                  border: 'none',
                  outline: 'none',
                  position: 'relative',
                }}
              >
                {item.id === 'access-management' && badgeCount > 0 && (
                  <span
                    className={`badge ${isActive ? 'bg-light text-danger' : 'bg-danger text-light'}`}
                    style={{ 
                      position: 'absolute',
                      top: '0.125rem',
                      right: '0.125rem',
                      fontSize: '0.7rem',
                      zIndex: 1,
                    }}
                  >
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center justify-content-center me-2" style={{ width: '16px', height: '16px' }}>
                    <i className={`${item.icon}`} style={ICON_STYLE} />
                  </div>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="fw-medium text-nowrap text-truncate">{item.label}</div>
                      <small className={`text-muted text-nowrap text-truncate d-block ${isActive ? 'text-white-50' : ''}`}
                        style={isActive ? { color: 'var(--text-light-80) !important' } : {}}>
                        {item.description}
                      </small>
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </nav>
      
      {/* Help & Support Button - Fixed at bottom */}
      <div className="p-2 border-top">
        <div className="list-group list-group-flush">
          <button
            className={`list-group-item list-group-item-action border-0 rounded support-item-enhanced ${
              pathname === '/help' ? 'active' : ''
            }`}
            onClick={() => handleNavClick('/help')}
            title="FAQ & help"
            style={{ 
              width: '100%',
              margin: '0',
              padding: '0.8125rem 1rem',
              boxSizing: 'border-box',
              border: 'none',
              outline: 'none',
            }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center justify-content-center me-2" style={{ width: '16px', height: '16px' }}>
                <i className="bi bi-question-circle" style={ICON_STYLE} />
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-medium text-nowrap text-truncate">Support</div>
                <small className={`text-muted text-nowrap text-truncate d-block ${pathname === '/help' ? 'text-white-50' : ''}`}
                  style={pathname === '/help' ? { color: 'var(--text-light-50)' } : {}}>
                  FAQ & help
                </small>
              </div>
            </div>
          </button>
        </div>
      </div>
      
    </div>
  )
}
export default LeftNavigation
